// GlobalStateContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { MenuItem } from '../../resources/types';

type GlobalState = {
  version: string; loading: boolean; JWT: string;
  returnUrl: string; idUser: number; data: Record<string, any>;
  menu: MenuItem[]; resources: Record<string, any>;
  parameters: Record<string, any>; counters: Record<string, any>;
  name: string; user: string; idAirport: number;
};

const GlobalStateContext = createContext<GlobalState | undefined>(undefined);

// Bridge Object
export const globalStateStore: {
  setVersion?: (v: string) => void;
  setLoading?: (v: boolean) => void;
  setJWT?: (v: string) => void;
  setReturnUrl?: (v: string) => void;
  setIdUser?: (v: number) => void;
  setData?: (v: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => void;
  setMenu?: (v: MenuItem[] | ((prev: MenuItem[]) => MenuItem[])) => void;
  setResources?: (v: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => void;
  setParameters?: (v: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => void;
  setCounters?: (v: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => void;
  setName?: (v: string) => void;
  setUser?: (v: string) => void;
  setIdAirport?: (v: number) => void;
} = {};

export const GlobalStateProvider = ({ children }: { children: React.ReactNode }) => {
  // --- Lazy Initialization (Load from LocalStorage ONCE on startup) ---
  const getStored = (key: string, fallback: any) => {
    const item = localStorage.getItem(key);
    if (!item) return fallback;
    try { return JSON.parse(item); } catch { return item; }
  };

  const [version, setVersionState] = useState(() => localStorage.getItem('version') || "0.0.0");
  const [loading, setLoadingState] = useState(false);
  const [JWT, setJWTState] = useState(() => localStorage.getItem('JWT') || "");
  const [returnUrl, setReturnUrlState] = useState(() => localStorage.getItem('returnUrl') || "");
  const [idUser, setIdUserState] = useState(() => Number(localStorage.getItem('idUser')) || 0);
  const [data, setDataState] = useState<Record<string, any>>(() => getStored('data', {}));
  const [menu, setMenuState] = useState<MenuItem[]>(() => getStored('menu', []));
  const [resources, setResourcesState] = useState<Record<string, any>>(() => getStored('resources', {}));
  const [parameters, setParametersState] = useState<Record<string, any>>(() => getStored('parameters', {}));
  const [counters, setCountersState] = useState<Record<string, any>>(() => getStored('counters', {}));
  const [name, setNameState] = useState(() => localStorage.getItem('name') || "");
  const [user, setDisplayUserState] = useState(() => localStorage.getItem('user') || "");
  const [idAirport, setIdAirportState] = useState(() => Number(localStorage.getItem('idAirport')) || 0);

  // --- Registration ---
  useEffect(() => {
    globalStateStore.setVersion = setVersionState;
    globalStateStore.setLoading = setLoadingState;
    globalStateStore.setJWT = setJWTState;
    globalStateStore.setReturnUrl = setReturnUrlState;
    globalStateStore.setIdUser = setIdUserState;
    globalStateStore.setData = setDataState;
    globalStateStore.setMenu = setMenuState;
    globalStateStore.setResources = setResourcesState;
    globalStateStore.setParameters = setParametersState;
    globalStateStore.setCounters = setCountersState;
    globalStateStore.setName = setNameState;
    globalStateStore.setUser = setDisplayUserState;
    globalStateStore.setIdAirport = setIdAirportState;

    return () => { Object.keys(globalStateStore).forEach(k => (globalStateStore as any)[k] = undefined); };
  }, []);

  // --- Persistence Sync (The Correct Way) ---
  // This ensures that even functional updates are saved to localStorage
  useEffect(() => { localStorage.setItem('version', version); }, [version]);
  useEffect(() => { localStorage.setItem('JWT', JWT); }, [JWT]);
  useEffect(() => { localStorage.setItem('returnUrl', returnUrl); }, [returnUrl]);
  useEffect(() => { localStorage.setItem('idUser', idUser.toString()); }, [idUser]);
  useEffect(() => { localStorage.setItem('data', JSON.stringify(data)); }, [data]);
  useEffect(() => { localStorage.setItem('menu', JSON.stringify(menu)); }, [menu]);
  useEffect(() => { localStorage.setItem('resources', JSON.stringify(resources)); }, [resources]);
  useEffect(() => { localStorage.setItem('parameters', JSON.stringify(parameters)); }, [parameters]);
  useEffect(() => { localStorage.setItem('counters', JSON.stringify(counters)); }, [counters]);
  useEffect(() => { localStorage.setItem('name', name); }, [name]);
  useEffect(() => { localStorage.setItem('user', user); }, [user]);
  useEffect(() => { localStorage.setItem('idAirport', idAirport.toString()); }, [idAirport]);

  return (
    <GlobalStateContext.Provider value={{
      version, loading, JWT, returnUrl, idUser, data, menu, resources, parameters, counters, name, user, idAirport
    }}>
      {children}
    </GlobalStateContext.Provider>
  );
};

export const useGlobalState = () => {
  const context = useContext(GlobalStateContext);
  if (!context) throw new Error("useGlobalState must be used within Provider");
  return context;
};